import { Notification } from "@/utils/notificationUtils";
import { openBlobFile } from "@/utils/utils";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { specialCommissionsApi } from "../../../../modules/commissions/special/api";
import {
  CreateUpdateSpecialCommissionsSettingsRule,
  SpecialCommissionsReportFilterExportRequest,
  SpecialCommissionsReportFilterPageRequest,
} from "../../../../modules/commissions/special/types";
import { EntityObject, TwoLevelEntityIdObject, TwoLevelEntityObject } from "../../../types";
import { COMMISSIONS_SETTINGS_QUERY_KEYS, SPECIAL_COMMISSIONS_QUERY_KEYS } from "./queryKeys";

export const useCreateSpecialCommissionsRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: SPECIAL_COMMISSIONS_QUERY_KEYS.createSpecialCommissionsRule(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateSpecialCommissionsSettingsRule>) => {
      const { data } = await specialCommissionsApi.createSpecialCommissionsRule(payload);

      return data;
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({
        queryKey: COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(mutatedPayload.id),
      });

      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateSpecialCommissionsRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: SPECIAL_COMMISSIONS_QUERY_KEYS.updateSpecialCommissionsRule(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: TwoLevelEntityObject<CreateUpdateSpecialCommissionsSettingsRule>) => {
      const { data } = await specialCommissionsApi.updateSpecialCommissionsRule(payload);

      return data;
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({
        queryKey: COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(mutatedPayload.id1),
      });

      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteSpecialCommissionsRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: SPECIAL_COMMISSIONS_QUERY_KEYS.deleteSpecialCommissionsRule(),
    mutationFn: (payload: TwoLevelEntityIdObject) => {
      return specialCommissionsApi.deleteSpecialCommissionsRule(payload);
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({
        queryKey: COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(mutatedPayload.id1),
      });

      Notification.itemDeletedNotification();
    },
  });
};

export const useGetSpecialCommissionsReport = (payload: SpecialCommissionsReportFilterPageRequest) => {
  return useQuery({
    queryKey: SPECIAL_COMMISSIONS_QUERY_KEYS.getSpecialCommissionsReport(payload),
    queryFn: async () => {
      const { data } = await specialCommissionsApi.getSpecialCommissionsReport(payload);

      return data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useDownloadSpecialCommissionsExport = () => {
  return useMutation({
    mutationKey: SPECIAL_COMMISSIONS_QUERY_KEYS.downloadSpecialCommissionsExport(),
    mutationFn: async (payload: SpecialCommissionsReportFilterExportRequest) => {
      const response = await specialCommissionsApi.downloadSpecialCommissionsExport(payload);
      openBlobFile(response);

      return response.data;
    },
  });
};
